import React, { useEffect, useRef, useState } from "react";
import { loadGoogleMaps, mapsAuthFailed } from "../lib/googleMaps";
import { MapPin, Navigation, Route, AlertCircle } from "lucide-react";

/**
 * RouteMap - Route visualization between a pickup and destination.
 *
 * • When REACT_APP_GOOGLE_MAPS_API_KEY is set: renders the real Google Maps
 *   Directions using the JavaScript API (lazy-loaded).
 * • When not set (default in preview): renders a MOCK map — an SVG "map" with
 *   a stylized route between two markers plus the distance/duration estimate.
 *
 * Props:
 *   pickup:      { label, lat?, lng? }   — origin
 *   destination: { label, lat?, lng? }   — trainer zone / drop-off
 *   travelMode:  "DRIVING" (default)
 */
export default function RouteMap({ pickup, destination, travelMode = "DRIVING", className = "" }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const isMock = !apiKey;
  const mapRef = useRef(null);
  const [error, setError] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [unavailable, setUnavailable] = useState(mapsAuthFailed());

  // ===== Live Google Maps (only when key is present) =====
  useEffect(() => {
    if (isMock) return;
    if (!pickup?.label || !destination?.label) return;
    let cancelled = false;

    (async () => {
      try {
        const google = await loadGoogleMaps();
        if (cancelled || !mapRef.current) return;
        const map = new google.maps.Map(mapRef.current, {
          center: { lat: 43.65107, lng: -79.347015 }, // Toronto default
          zoom: 11, disableDefaultUI: false,
        });
        const svc = new google.maps.DirectionsService();
        const renderer = new google.maps.DirectionsRenderer({ map });
        svc.route({
          origin: pickup.label + ", Canada",
          destination: destination.label + ", Canada",
          travelMode: google.maps.TravelMode[travelMode],
        }, (result, status) => {
          if (cancelled) return;
          if (status === "OK") {
            renderer.setDirections(result);
            const leg = result.routes[0]?.legs[0];
            if (leg) setRouteInfo({ distance: leg.distance.text, duration: leg.duration.text });
          } else {
            setError(`Directions failed: ${status}`);
          }
        });
      } catch (e) {
        // A rejected key, disabled billing or an offline client must not leave
        // an empty map behind — drop back to the mock preview.
        if (!cancelled) setUnavailable(true);
      }
    })();

    const onAuthFail = () => setUnavailable(true);
    window.addEventListener("gmaps-auth-failed", onAuthFail);
    return () => {
      cancelled = true;
      window.removeEventListener("gmaps-auth-failed", onAuthFail);
    };
  }, [apiKey, isMock, pickup?.label, destination?.label, travelMode]);

  // ===== MOCK (no API key, or Google unavailable) =====
  if (isMock || unavailable) {
    return (
      <MockRouteMap
        pickup={pickup}
        destination={destination}
        travelMode={travelMode}
        className={className}
        reason={isMock ? "no-key" : "unavailable"}
      />
    );
  }

  // ===== LIVE =====
  return (
    <div className={`border border-border bg-card overflow-hidden ${className}`} data-testid="route-map-live">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Route className="w-4 h-4 text-primary" />
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Route preview</div>
        </div>
        {routeInfo && (
          <div className="text-xs font-mono" data-testid="route-info-live">
            <span className="text-muted-foreground">Distance</span> <b>{routeInfo.distance}</b> &middot;{" "}
            <span className="text-muted-foreground">Time</span> <b>{routeInfo.duration}</b>
          </div>
        )}
      </div>
      <div ref={mapRef} className="w-full h-72" data-testid="route-map-canvas" />
      {error && (
        <div className="px-4 py-2 text-xs text-destructive border-t border-border flex items-center gap-2" data-testid="route-map-error">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </div>
      )}
    </div>
  );
}

// ------------------------ MOCK ------------------------

function estimateRouteMetrics(a, b) {
  // Simple deterministic estimate based on label lengths, so it feels stable per booking.
  const seed = (a + "→" + b).split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  const km = 5 + (seed % 42);            // 5–47 km
  const minutes = 12 + (seed % 55);      // 12–66 min
  return { km, minutes };
}

function MockRouteMap({ pickup, destination, travelMode, className, reason = "no-key" }) {
  const p = pickup?.label || "Pickup";
  const d = destination?.label || "Destination";
  const { km, minutes } = estimateRouteMetrics(p, d);

  // Random-ish but deterministic route control points inside the SVG viewbox
  const seed = (p + d).split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  const c1x = 200 + ((seed * 7) % 120);
  const c1y = 80 + ((seed * 11) % 60);
  const c2x = 340 + ((seed * 13) % 100);
  const c2y = 200 + ((seed * 17) % 40);

  return (
    <div className={`border border-border bg-card overflow-hidden ${className}`} data-testid="route-map-mock">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Route className="w-4 h-4 text-primary" />
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Route preview</div>
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 border border-amber-500/60 text-amber-600 bg-amber-500/10" data-testid="route-map-mock-badge" title={reason === "unavailable" ? "Google Maps could not be loaded for this domain — showing an illustrative preview" : "No Google Maps key configured — showing an illustrative preview"}>
            {reason === "unavailable" ? "Preview · live map unavailable here" : "Mock · add API key to enable live map"}
          </span>
        </div>
        <div className="text-xs font-mono" data-testid="route-info-mock">
          <span className="text-muted-foreground">Distance</span> <b>~{km} km</b> &middot;{" "}
          <span className="text-muted-foreground">Time</span> <b>~{minutes} min</b> &middot;{" "}
          <span className="text-muted-foreground">Mode</span> <b>{travelMode.toLowerCase()}</b>
        </div>
      </div>

      <svg
        viewBox="0 0 640 288"
        className="w-full h-72 bg-[#e9ecef]"
        role="img"
        aria-label={`Mock route map from ${p} to ${d}`}
        data-testid="route-map-mock-svg"
      >
        {/* Grid background evoking a street map */}
        <defs>
          <pattern id="mockGrid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#cfd4d9" strokeWidth="0.5" />
          </pattern>
          <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c9dfe8" />
            <stop offset="100%" stopColor="#a9c8d4" />
          </linearGradient>
        </defs>
        <rect width="640" height="288" fill="url(#mockGrid)" />
        {/* Fake landmass / water */}
        <path d="M0,220 C120,180 220,240 340,210 C460,180 560,230 640,200 L640,288 L0,288 Z" fill="url(#waterGrad)" />
        {/* Fake highway strokes */}
        <path d="M-20,120 L680,150" stroke="#fff" strokeWidth="6" opacity="0.85" />
        <path d="M-20,120 L680,150" stroke="#f4c542" strokeWidth="1" strokeDasharray="10 8" />
        <path d="M120,-20 L200,300" stroke="#fff" strokeWidth="5" opacity="0.85" />
        <path d="M460,-20 L540,300" stroke="#fff" strokeWidth="5" opacity="0.85" />

        {/* The route line — pickup to destination */}
        <path
          d={`M 90 200 Q ${c1x} ${c1y}, ${c2x} ${c2y} T 560 100`}
          fill="none"
          stroke="#FF4B2B"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="0"
          data-testid="route-map-mock-line"
        />
        {/* Direction arrow near end */}
        <polygon points="555,95 570,102 555,109" fill="#FF4B2B" />

        {/* Origin marker */}
        <g transform="translate(90 200)" data-testid="route-map-mock-origin">
          <circle r="14" fill="#fff" />
          <circle r="10" fill="#22c55e" />
          <text x="22" y="6" fontFamily="Arial" fontSize="13" fontWeight="700" fill="#0a0a0a">A</text>
        </g>
        {/* Destination marker */}
        <g transform="translate(560 100)" data-testid="route-map-mock-destination">
          <circle r="14" fill="#fff" />
          <circle r="10" fill="#FF4B2B" />
          <text x="-32" y="6" fontFamily="Arial" fontSize="13" fontWeight="700" fill="#0a0a0a">B</text>
        </g>

        {/* Attribution */}
        <text x="12" y="278" fontFamily="Arial" fontSize="10" fill="#6b7280">Mock map · DriveTerrain</text>
      </svg>

      <div className="px-4 py-3 border-t border-border grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3 h-3 text-emerald-500" /> Pickup (A)
          </div>
          <div className="font-medium truncate" data-testid="route-mock-origin-label">{p}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <Navigation className="w-3 h-3 text-primary" /> Destination (B)
          </div>
          <div className="font-medium truncate" data-testid="route-mock-destination-label">{d}</div>
        </div>
      </div>
    </div>
  );
}
